import { FC, ReactNode } from 'react';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { TableRow } from '../../../components/table/index';
import { Tooltip } from '../../../components/tooltip/index';
import { useFormat } from '../../../hooks/index';
import { selectedCompanyStorage } from '../../../storage/company/index';
import { formatDisplayValue } from '../../../utils/format-display-value.util';
import { fromBackendDateFormat } from '../../../utils/from-backend-date-format.util';
import { ReverseVestingRow, ReverseVestingTermination } from '../hooks/index';
import {
  terminationEventCardRowOrderMap,
  TerminationEventColumn,
  terminationEventColumnOrderMap,
  terminationEventColumnTranslationKey,
} from './reverse-vesting.types';
import { ReverseVestingCard } from './reverse-vesting-card';

export type TerminationEventItemProps = {
  data: { termination: ReverseVestingTermination } & ReverseVestingRow;
  mode?: 'TABLE';
};

export const TerminationEventItem: FC<TerminationEventItemProps> = ({
  data: { id, reverseVestingShares, shareClassName, termination, valuationName },
  mode,
}) => {
  const company = selectedCompanyStorage(({ company }) => company);
  const { format } = useFormat();

  const currency = company?.currency?.symbol || company?.currency?.iso3.toUpperCase();

  // Company incorporation has no valuation event of its own — the backend sends null for it.
  const eventName = valuationName ? (
    <Tooltip content={valuationName}>
      <span className="block max-w-[150px] truncate">{valuationName}</span>
    </Tooltip>
  ) : (
    <AppFormattedMessage id="COMPANY_INCORPORATION" />
  );

  const payload: { id: string } & Record<TerminationEventColumn, number | ReactNode | string> = {
    [TerminationEventColumn.TERMINATION_DATE]: format(
      fromBackendDateFormat(termination.terminationDate),
      'dd/MM/yyyy',
    ),
    [TerminationEventColumn.VALUATION_EVENT]: eventName,
    [TerminationEventColumn.SHARE_CLASS]: (
      <Tooltip content={shareClassName}>
        <span className="block max-w-[150px] truncate">{shareClassName}</span>
      </Tooltip>
    ),
    // Only the reverse vesting shares were settled at termination (final vested + lapsed), so the
    // row mirrors "No of shares" in the reverse vesting table rather than the whole issuance.
    [TerminationEventColumn.SHARES_ISSUED]: formatDisplayValue(reverseVestingShares),
    [TerminationEventColumn.FINAL_VESTED]: formatDisplayValue(termination.vestedShares),
    [TerminationEventColumn.LAPSED]: formatDisplayValue(termination.lapsedShares),
    // Repurchase prices are frequently par values, so the full 7 decimals have to survive here —
    // the default 2 would render a real 0.0001 price as 0.
    [TerminationEventColumn.REPURCHASE_PRICE]: formatDisplayValue(termination.repurchasePrice, {
      roundTo: 7,
    }),
    [TerminationEventColumn.COMPENSATION]: formatDisplayValue(termination.compensation, {
      prefix: currency,
      roundTo: 2,
    }),
    id,
  };

  if (mode === 'TABLE')
    return (
      <TableRow
        columnOrder={terminationEventColumnOrderMap}
        fields={payload}
        labelClassName="text-xs font-[450] text-gray-700"
      />
    );

  return (
    <ReverseVestingCard
      rows={terminationEventCardRowOrderMap.map((column) => ({
        id: column,
        label: <AppFormattedMessage id={terminationEventColumnTranslationKey[column]} />,
        value: <span>{payload[column]}</span>,
      }))}
      subtitle={shareClassName}
      title={eventName}
    />
  );
};
